import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import getDecodeDescription from "../../services/decodeText";
import { ModalConsumer } from "../../common/ModalContext";
import ModalAdditionResult from "../../components/ModalAdditionResult/ModalAdditionResult";
import { getSpotsByPropValueAPI } from "../../services/api";
import StepsForm from "../StepsForm/StepsForm";
import FirstStep from "./FirstStep";
import SecondStep from "./SecondStep";
import ThirdStep from "./ThirdStep";
import {
  checkIsValidFormSteps,
  updateErrorsFormSteps,
  getIsRegistered,
} from "../../services/manageValidation";
import styles from "./SpotForm.module.scss";

const SpotForm = ({
  handleSubmit,
  type,
  spotToEdit,
  clearAll,
  isSpotEditor,
  getItemToEdit,
  stepStatus,
}) => {
  const {
    name: spotToEditName,
    country: spotToEditCountry,
    state: spotToEditState,
    description: spotToEditDescription,
    logo: spotToEditLogo,
    link: spotToEditLink,
    address: spotToEditAddress,
    email: spotToEditEmail,
    category: spotToEditCategory,
    fields: spotToEditFields,
    titleSeo: spotToEditTitleSeo,
    descriptionSeo: spotToEditDescriptionSeo,
    userOwner: spotToEditUserOwner,
    phone: spotToEditPhone,
    employeesNumber: spotToEditEmployeesNumber,
    languages: spotToEditLanguages,
    foundationDate: spotToEditFoundationDate,
    socialMedia,
    ...rest
  } = spotToEdit;
  delete rest.originalId;

  useEffect(() => {
    if (!spotToEdit) {
      return;
    }
    setName(spotToEditName);
    spotToEditDescription &&
      setDescription(getDecodeDescription(spotToEditDescription));
    spotToEditLogo && setLogo(spotToEditLogo);
    spotToEditAddress && setAddress(spotToEditAddress);
    spotToEditLink && setWebsite(spotToEditLink);
    const primary =
      spotToEdit.addresses?.find((office) => office.primary) ||
      spotToEdit.addresses?.[0];
    const officeCountry = primary?.country || spotToEditCountry || "";
    setCountries(officeCountry ? [officeCountry] : []);
    if (officeCountry === "USA") {
      const officeState = primary?.state || spotToEditState || "";
      setUsState(officeState ? [officeState] : []);
    } else {
      setUsState([]);
    }
    if (primary) {
      setAddress(primary.line1 || spotToEditAddress || "");
      setLine2(primary.line2 || "");
      setCity(primary.city || spotToEdit.city || "");
      setPostcode(primary.postcode || "");
      setExtraAddresses(
        (spotToEdit.addresses || [])
          .filter((office) => office !== primary)
          .map((office) => ({ ...office, primary: false }))
      );
    } else {
      spotToEditAddress && setAddress(spotToEditAddress);
      spotToEdit.city && setCity(spotToEdit.city);
    }
    setHeadquartersHere(true);
    setEmail(primary?.email || spotToEditEmail || "");
    type === "business" &&
      spotToEditCategory &&
      setCategory(spotToEditCategory);
    type === "business" &&
      spotToEditFields &&
      setBusinessFields(spotToEditFields);
    type === "NGO" &&
      spotToEditFields &&
      setOrganizationFields(spotToEditFields);
    spotToEditTitleSeo && setTitleSeo(spotToEditTitleSeo);
    spotToEditDescriptionSeo && setDescriptionSeo(spotToEditDescriptionSeo);
    setPhone(primary?.phone || spotToEditPhone || "");
    spotToEditEmployeesNumber && setEmployees([spotToEditEmployeesNumber]);
    socialMedia?.facebook && setFacebook(socialMedia.facebook);
    socialMedia?.linkedin && setLinkedin(socialMedia.linkedin);
    spotToEditFoundationDate && setFoundationDate(spotToEditFoundationDate);
    spotToEditLanguages && setLanguages(spotToEditLanguages);
  }, [spotToEdit, type]);

  const [countries, setCountries] = useState([]);
  const [usState, setUsState] = useState([]);
  const [organizationFields, setOrganizationFields] = useState([]);
  const [businessFields, setBusinessFields] = useState([]);
  const [validationError, setValidationError] = useState([]);
  const [photo, setPhoto] = useState("");
  const [relatedOrganizations, setRelatedOrganizations] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState([]);
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [extraAddresses, setExtraAddresses] = useState([]);
  const [headquartersHere, setHeadquartersHere] = useState(true);
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState("");
  const [titleSeo, setTitleSeo] = useState("");
  const [descriptionSeo, setDescriptionSeo] = useState("");
  const [spotsByName, setSpotsByName] = useState([]);
  const [spotsByUrl, setSpotsByUrl] = useState([]);
  const [spotExist, setSpotExist] = useState(false);
  const [spotURLExist, setSpotURLExist] = useState(false);
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [facebook, setFacebook] = useState("");
  const [foundationDate, setFoundationDate] = useState("");
  const [employees, setEmployees] = useState([]);
  const [languages, setLanguages] = useState([]);

  const cancelValidationError = (filterType) => {
    // MOVE TO SERVICES?
    setValidationError(
      validationError.filter((errorItem) => errorItem !== filterType)
    );
  };

  const changeValidationError = (filterType) => {
    // MOVE TO SERVICES?
    setValidationError(
      validationError.filter((errorItem) => errorItem !== filterType)
    );
  };

  const onFileChange = (file) => {
    if (!file) {
      setLogo("");
      return;
    }

    if (file.size / 1024 > 100) {
      setLogo("");
      setValidationError((prevValidationError) => [
        ...prevValidationError,
        "logoSize",
      ]);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setLogo(reader.result);

      setValidationError(
        validationError.filter((errorItem) => errorItem !== "logoSize")
      );
    };
  };

  useEffect(() => {
    checkSpotExist();
  }, [spotsByName, spotsByUrl, spotToEdit]);

  const checkSpotExist = () => {
    if (!!spotToEdit) {
      setSpotExist(false);
      return;
    }
    setSpotExist(!!spotsByName?.length && !!spotsByUrl?.length);
  };

  const checkSpot = (prop, value, setProp) => {
    if (!value) {
      setProp([]);
      return;
    }

    getSpotsByPropValueAPI({ prop, value }).then((resp) => {
      setProp(resp?.data);
    });
  };

  async function manageNextStep(step, toggleModal) {
    const state = {
      name,
      countries,
      usState,
      address,
      organizationFields,
      category,
      businessFields,
      email,
      website,
      photo,
      spotsByUrl,
      type: type,
      isNew: !spotToEdit,
    };

    if (step === 1) {
      const resp =
        website &&
        (await getSpotsByPropValueAPI({ prop: "url", value: website }));
      const isURLExists = resp?.data && getIsRegistered(resp?.data, website);

      setSpotURLExist(isURLExists);
      const isValid = checkIsValidFormSteps({ state, step });
      if (!isValid) {
        updateErrorsFormSteps({
          state,
          step,

          setValidationError,
        });
        return false;
      }
      if (isValid) {
        return true;
      }
    }

    if (step === 2) {
      if (!spotToEdit) {
        const stateForSecondStep = {
          name,
          countries,
          usState,
          address,
          organizationFields,
          category,
          businessFields,
          email,
          website,
          photo,
          type: type,
          isNew: !spotToEdit,
        };
        const isValid = checkIsValidFormSteps({
          state: stateForSecondStep,
          step,
        });

        if (!isValid) {
          updateErrorsFormSteps({
            state: stateForSecondStep,
            step,
            setValidationError,
          });
          return false;
        }

        if (isValid) {
          return true;
        }
      } else {
        const isValid = checkIsValidFormSteps({ state, step });
        if (!isValid) {
          updateErrorsFormSteps({
            state,
            step,
            setValidationError,
          });
          return false;
        }
        if (isValid) {
          return true;
        }
      }
    }

    if (step === 3) {
      const isValid = checkIsValidFormSteps({ state, step });
      if (!isValid) {
        updateErrorsFormSteps({
          state,
          step,
          setValidationError,
        });
        return false;
      }

      if (isValid) {
        const savedPrimary =
          spotToEdit?.addresses?.find((office) => office.primary) ||
          spotToEdit?.addresses?.[0];
        const nextCountry = countries?.[0] || "";
        const nextState = nextCountry === "USA" ? usState?.[0] || "" : "";
        const sameText = (left, right) =>
          String(left || "").trim() === String(right || "").trim();
        const primaryUnchanged =
          savedPrimary &&
          sameText(savedPrimary.line1 || spotToEdit.address, address) &&
          sameText(savedPrimary.line2, line2) &&
          sameText(savedPrimary.city || spotToEdit.city, city) &&
          sameText(savedPrimary.postcode, postcode) &&
          sameText(savedPrimary.country || spotToEdit.country, nextCountry) &&
          sameText(
            savedPrimary.state ||
              (spotToEdit.country === "USA" ? spotToEdit.state : ""),
            nextState
          );
        const keptPoint = primaryUnchanged
          ? {
              location: savedPrimary.location || spotToEdit.location,
              loc: savedPrimary.loc || spotToEdit.loc,
            }
          : {};
        const firstOffice = {
          country: nextCountry,
          state: nextState,
          city,
          line1: address,
          line2,
          postcode,
          phone,
          email,
          primary: headquartersHere,
          ...keptPoint,
        };
        const extras = (extraAddresses || []).map((office) => ({
          ...office,
          primary: !headquartersHere && Boolean(office.primary),
        }));
        let nextAddresses = [firstOffice, ...extras];
        if (!nextAddresses.some((office) => office.primary)) {
          nextAddresses = nextAddresses.map((office, index) => ({
            ...office,
            primary: index === 0,
          }));
        }
        const headquarters =
          nextAddresses.find((office) => office.primary) || nextAddresses[0];
        nextAddresses = [
          headquarters,
          ...nextAddresses.filter((office) => office !== headquarters),
        ];
        const savedAuthor = [spotToEdit?.author, spotToEdit?.userOwner].find(
          (value) => String(value || "").includes("@")
        );
        handleSubmit({
          ...rest,
          toggleModal,
          name,
          author: savedAuthor || "",
          countries,
          usState,
          address,
          line2,
          city,
          postcode,
          addresses: nextAddresses,
          description: encodeURIComponent(description),
          logo,
          email: headquarters.email || email,
          businessFields,
          category,
          organizationFields,
          type: type,
          status: spotToEdit.status,
          photo,
          website,
          titleSeo,
          descriptionSeo,
          originalId: isSpotEditor ? spotToEdit._id : spotToEdit.originalId,
          _id: spotToEdit._id,
          userOwner: spotToEdit?.userOwner ? spotToEdit.userOwner : "",
          phone: headquarters.phone || phone,
          employeesNumber: employees[0],
          languages,
          socialMedia: {
            linkedin,
            facebook,
          },
          foundationDate,
          state: nextState,
          setName,
          setCountries,
          setUsState,
          setAddress,
          setEmail,
          setOrganizationFields,
          setBusinessFields,
          setPhoto,
          setDescription,
          setCategory,
          setWebsite,
          setLogo,
          setRelatedOrganizations,
          setValidationError,
          setTitleSeo,
          setDescriptionSeo,
          setPhone,
          setLinkedin,
          setFacebook,
          setFoundationDate,
          setEmployees,
          setLanguages,
        });
        if (spotToEdit)
          clearAll({
            setOrganizationFields,
            setBusinessFields,
            setValidationError,
            setName,
            setCategory,
            setWebsite,
            setAddress,
            setDescription,
            setCountries,
            setUsState,
            setLogo,
            setEmail,
            setRelatedOrganizations,
            setSpotsByName,
            setSpotsByUrl,
            setTitleSeo,
            setDescriptionSeo,
          });
      }
    }
  }

  const firstStepState = {
    name,
    setName,
    category,
    setCategory,
    businessFields,
    setBusinessFields,
    organizationFields,
    setOrganizationFields,
    description,
    setDescription,
    validationError,
    setValidationError,
    spotsByName,
    setSpotsByName,
    getItemToEdit,
    website,
    setWebsite,
    email,
    setEmail,
    spotsByUrl,
    setSpotsByUrl,
    spotToEdit,
    checkSpot,
    spotURLExist,
    //onFileChange,
    changeValidationError,
    cancelValidationError,
    type,
  };
  const secondStepState = {
    countries,
    setCountries,
    usState,
    setUsState,
    address,
    setAddress,
    line2,
    setLine2,
    city,
    setCity,
    postcode,
    setPostcode,
    extraAddresses,
    setExtraAddresses,
    validationError,
    setValidationError,
    // spotsByUrl, setSpotsByUrl,
    getItemToEdit,
    // setSpotType,
    spotToEdit,
    checkSpot,
    changeValidationError,
    cancelValidationError,
    // spotURLExist,
    phone,
    setPhone,
    email,
    setEmail,
    headquartersHere,
    setHeadquartersHere,
    linkedin,
    setLinkedin,
    facebook,
    setFacebook,
    // type
  };
  const thirdStepState = {
    logo,
    titleSeo,
    setTitleSeo,
    descriptionSeo,
    setDescriptionSeo,
    validationError,
    setValidationError,
    onFileChange,
    foundationDate,
    setFoundationDate,
    languages,
    setLanguages,
    employees,
    setEmployees,
  };
  return (
    <div className={styles.container}>
      <StepsForm
        type={type}
        manageNextStep={manageNextStep}
        firstStepFields={<FirstStep state={firstStepState} />}
        secondStepFields={<SecondStep state={secondStepState} />}
        thirdStepFields={<ThirdStep state={thirdStepState} />}
        stepsAmount={3}
        resetStep={stepStatus}
      />
      <ModalConsumer>
        {({ isOpen, toggleModal, modalId }) => {
          return (
            <>
              {modalId === "organization" && (
                <ModalAdditionResult
                  toggleModal={toggleModal}
                  isOpen={isOpen}
                  content="You've added a spot"
                />
              )}

              {modalId === "wrongAddress" && (
                <ModalAdditionResult
                  toggleModal={toggleModal}
                  isOpen={isOpen}
                  content="Address is wrong. Please change the address and try again"
                />
              )}
            </>
          );
        }}
      </ModalConsumer>
    </div>
  );
};

SpotForm.propTypes = {
  lng: PropTypes.object,
  handleSubmit: PropTypes.func,
  type: PropTypes.string,
  spotToEdit: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  clearAll: PropTypes.func,
  isSpotEditor: PropTypes.bool,
};

export default SpotForm;
